/**
 * Movepro CRM API interface
 *
 * @package Movepro CRM
 * @module BulkImport
 */

const ROUTE = (model: string) => `/bulk-imports/${model}`;

/** Status of an import run. Null when the user has no run for this model. */
export type ImportStatus = 'queued' | 'validating' | 'importing' | 'succeeded' | 'failed' | null;

export interface ImportFailure {
    row: number;
    column: string | null;
    message: string;
}

export interface ImportRunState {
    status: ImportStatus;
    rows_total?: number;
    rows_done?: number;
    created?: number;
    failures?: ImportFailure[];
    message?: string | null;
    updated_at?: string;
    /**
     * Monotonic per run, incremented on every server-side state write. Present
     * on both the polled status and the broadcast, so a consumer can tell a
     * newer state from an older one where `updated_at` is too coarse. Absent
     * only when the server has forgotten the run.
     */
    sequence?: number;
}

/** A run that has finished, either way — nothing more will change. */
export function isTerminal(state: ImportRunState | null): boolean {
    return state?.status === 'succeeded' || state?.status === 'failed';
}

/** A run still being worked on, so the interface should keep polling. */
export function isRunning(state: ImportRunState | null): boolean {
    return state?.status === 'queued' || state?.status === 'validating' || state?.status === 'importing';
}

/**
 * Download the template workbook for this model. Built per tenant, so it must
 * be fetched rather than linked to a static file.
 */
export async function downloadTemplate(model: string): Promise<Blob> {
    const { data } = await window.tenantAxios.get(`${ROUTE(model)}/template`, {
        responseType: 'blob',
    });

    return data;
}

/**
 * Start an import of a workbook already uploaded to S3.
 *
 * `key` comes from the Vapor signed upload — the file itself is never posted
 * to the API, because Lambda's request payload cap is below a large workbook.
 */
export async function startImport(model: string, key: string): Promise<ImportRunState> {
    const { data } = await window.tenantAxios.post(ROUTE(model), { key });

    return data;
}

/** The current user's run state for this model, in progress or finished. */
export async function getStatus(model: string): Promise<ImportRunState> {
    const { data } = await window.tenantAxios.get(`${ROUTE(model)}/status`);

    return data;
}

/** Forget a finished run, so returning to the page does not reattach to it. */
export async function clearStatus(model: string): Promise<void> {
    await window.tenantAxios.delete(`${ROUTE(model)}/status`);
}
